import dayjs from 'dayjs';
import type {TankGaugeReading} from '@/types/well';
import {priorGaugeReading} from '@/lib/tankGaugeMapping';

/**
 * Chunk 7 of the tank-gauge/pulled-bottoms feature set (2026-09-30) — the
 * "unexplained drop" flag. Full spec confirmed with the client back in
 * chunk 1's original design doc (see project memory), re-confirmed with
 * Duane via this chunk's own clarifying questions:
 *   - A tank is flagged when its gauge reading drops (in bbls, via the
 *     tank's own multiplier — same conversion computeOilProductionFromGaugeDelta
 *     already uses) by >= TANK_DROP_THRESHOLD_BBLS from one day to the next.
 *   - "Explained" means a Run Ticket or Pulled Bottoms record exists for
 *     that SAME tank, dated EXACTLY the drop's own date (the day the
 *     lower reading was recorded) — a late entry is fine, but it has to
 *     be dated that day, not just fall within some grace window.
 *   - Admin/office only (this is an office-side review tool, not
 *     something a field pumper sees or acts on) — see
 *     useTankDropReconciliation.ts's own gating.
 */
export const TANK_DROP_THRESHOLD_BBLS = 15;

export interface TankDropTank {
   id: string;
   tankNumber: string;
   leaseId: string;
   multiplier: number;
}

/** One qualifying (>= TANK_DROP_THRESHOLD_BBLS) day-over-day drop for one tank. */
export interface TankDropEvent {
   tankId: string;
   tankNumber: string;
   leaseId: string;
   /** The date the LOWER (post-drop) reading was recorded — the date a Run Ticket/Pulled Bottoms record must match to "explain" this drop. */
   dropDate: string;
   /** The date of the reading being dropped FROM — informational only, not part of the explanation match. */
   priorDate: string;
   dropBbls: number;
}

/**
 * Scans one tank's own readings for every day-over-day drop within
 * `windowDates` (any order — typically the last MAX_BACKDATE_DAYS
 * calendar dates, see useTankDropReconciliation.ts) that meets
 * TANK_DROP_THRESHOLD_BBLS. Reuses priorGaugeReading (tankGaugeMapping.ts)
 * for the "strictly before" lookup — same semantics the Daily Report
 * drawer's own gauge-delta calc already relies on — so a day with no
 * prior reading at all (the tank's first-ever reading, or a gap before
 * one was ever recorded) is silently skipped: there's nothing to diff it
 * against, so nothing to flag. A day whose gauge went UP (or stayed flat)
 * is also not a drop at all, let alone an unexplained one — normal
 * accumulating production, nothing to explain.
 */
export function computeTankDropEvents(
   tank: TankDropTank,
   readings: TankGaugeReading[],
   windowDates: string[],
): TankDropEvent[] {
   const events: TankDropEvent[] = [];
   for (const date of windowDates) {
      const today = readings.find((r) => r.gaugeDate === date);
      if (!today) continue;
      const prior = priorGaugeReading(readings, date);
      if (!prior) continue;
      const dropBbls = Math.round((prior.gaugeInches - today.gaugeInches) * tank.multiplier * 100) / 100;
      if (dropBbls >= TANK_DROP_THRESHOLD_BBLS) {
         events.push({
            tankId: tank.id,
            tankNumber: tank.tankNumber,
            leaseId: tank.leaseId,
            dropDate: date,
            priorDate: prior.gaugeDate,
            dropBbls,
         });
      }
   }
   return events;
}

/**
 * Live, single-reading version of computeTankDropEvents' threshold check,
 * for the Daily Report's inline banner (components/TankDropBanner.tsx):
 * the barrels the tank dropped from `priorGaugeInches` to
 * `todayGaugeInches` (same rounding/formula as computeTankDropEvents), or
 * null when it didn't drop by at least TANK_DROP_THRESHOLD_BBLS.
 */
export function liveTankDropBbls(
   priorGaugeInches: number,
   todayGaugeInches: number,
   tankMultiplier: number,
): number | null {
   const dropBbls = Math.round((priorGaugeInches - todayGaugeInches) * tankMultiplier * 100) / 100;
   return dropBbls >= TANK_DROP_THRESHOLD_BBLS ? dropBbls : null;
}

function normalizeTankNumber(s: string): string {
   return s.trim().toLowerCase();
}

export interface RunTicketTankCandidate {
   id: string;
   ticketNumber: string;
   /** RunTicket.tank — free text (pumper-typed or AI-scan-read), not a real foreign key. */
   tank: string;
}

export interface TankDropExplanation {
   explained: boolean;
   /**
    * Present only when unexplained AND a same-day Run Ticket exists for
    * this drop's LEASE but its free-text `tank` field named some OTHER
    * tank (so the single-tank-lease fallback below didn't apply either)
    * — Duane's call, 2026-09-30: note it right on the flag itself rather
    * than silently dropping it or raising a separate ticket-level flag.
    */
   mismatchNote?: string;
}

/**
 * Decides whether one TankDropEvent counts as "explained," per the
 * client-confirmed spec above.
 *
 * RunTicket has no real tank foreign key (see types/well.ts's own doc
 * comment on RunTicket.tank) — matched here by trimmed/case-insensitive
 * comparison against Tank.tankNumber, with one fallback Duane confirmed
 * 2026-09-30: on a lease with exactly one active tank, ANY same-day
 * ticket for that lease counts regardless of what its tank field says —
 * there's nothing else it could be, same trivial case
 * useLeaseTankGauge.ts's isTrivialSingleTank already special-cases for
 * the Daily Report drawer.
 *
 * PulledBottom has a real tankId FK (unlike RunTicket), so that half of
 * the check is a plain equality match by the caller — no fuzzy logic
 * needed, hence `sameDayTankPulledBottoms` here is just a presence check.
 *
 * Callers MUST already scope `sameDayLeaseTickets` to this drop's own
 * leaseId (resolved via each ticket's wellId -> Well.leaseId — NEVER
 * RunTicket.leaseId itself, a distinct legacy numeric field unrelated to
 * the real Lease entity, see lib/runTicketMapping.ts) and to dropDate as
 * a LOCAL calendar date (matching useRunTicketReconciliation.ts's own
 * 2026-09-25 UTC-offset fix — see this file's own localCalendarDate).
 */
export function explainTankDrop(params: {
   tank: {tankNumber: string};
   activeTankCountOnLease: number;
   sameDayLeaseTickets: RunTicketTankCandidate[];
   sameDayTankPulledBottoms: {id: string}[];
}): TankDropExplanation {
   const {tank, activeTankCountOnLease, sameDayLeaseTickets, sameDayTankPulledBottoms} = params;

   if (sameDayTankPulledBottoms.length > 0) return {explained: true};

   const singleTankLease = activeTankCountOnLease <= 1;
   const matched = sameDayLeaseTickets.find(
      (t) => singleTankLease || normalizeTankNumber(t.tank) === normalizeTankNumber(tank.tankNumber),
   );
   if (matched) return {explained: true};

   if (sameDayLeaseTickets.length === 0) return {explained: false};

   // 2+ tanks on this lease (the single-tank fallback above didn't
   // apply), and every same-day ticket named some OTHER tank — flag it,
   // but note the mismatch right on the flag.
   const names = sameDayLeaseTickets.map((t) => `#${t.ticketNumber || t.id}: Tank ${t.tank || '(blank)'}`).join(', ');
   return {
      explained: false,
      mismatchNote: `Same-day run ticket${sameDayLeaseTickets.length > 1 ? 's' : ''} on this lease list a different tank (${names}) — verify before treating this as unexplained.`,
   };
}

/**
 * A RunTicket's pickupDate is a UTC Timestamp (time-of-day included), but
 * every date this feature (and useRunTicketReconciliation.ts, and
 * gaugeDate itself) reasons about is a LOCAL calendar date. Converting
 * through dayjs (not a raw ISO slice) is what makes this match
 * useRunTicketReconciliation.ts's own 2026-09-25 fix — see that file's
 * comment on the bug a naive UTC slice caused for anyone west of
 * Greenwich.
 */
export function localCalendarDate(isoTimestamp: string): string {
   return dayjs(isoTimestamp).format('YYYY-MM-DD');
}
